import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  BrainCircuit,
  Boxes,
  CheckCircle2,
  AlertCircle,
  Image,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  ListFilter,
  Mic,
  RefreshCw,
  Save,
  Search,
  ServerCog,
  Video,
  Volume2,
  Workflow,
  XCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { getAdminToken } from '@/api/adminApi'
import { friendlyMessage } from '@/api/http'
import {
  discoverModels,
  getModelBackendHealth,
  getModels,
  probeModelBackend,
  putCategory,
  type ModelBackendHealthItem,
  type ModelBackendHealthResponse,
  type ModelBackendMeta,
} from '@/api/modelSettingsApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

type EditableValue = string | number | boolean | null

interface CategoryDefinition {
  key: string
  multiBackend: boolean
  apiKeyEditable: boolean
  fields: string[]
}

interface BackendEditor {
  category: string
  backendName: string
  meta?: ModelBackendMeta
  originalConfig: Record<string, EditableValue>
  config: Record<string, EditableValue>
  maskedApiKey: string
  apiKeyValue: string
  apiKeyTouched: boolean
  showApiKey: boolean
  discoveredModels: string[]
  isTesting: boolean
  isDiscovering: boolean
  isSaving: boolean
  testResult: { ok: boolean; disabled?: boolean; unsupported?: boolean; message?: string } | null
}

function healthKey(category: string, backendName: string): string {
  return `${category}\u0000${backendName}`
}

const CATEGORY_DEFINITIONS: CategoryDefinition[] = [
  { key: 'llm', multiBackend: true, apiKeyEditable: true, fields: ['base_url', 'model'] },
  { key: 'embed', multiBackend: true, apiKeyEditable: true, fields: ['base_url', 'model', 'dim'] },
  { key: 'image', multiBackend: false, apiKeyEditable: true, fields: ['base_url', 'provider', 'primary_model', 'prompt_suffix', 'primary_timeout_sec'] },
  { key: 'audio', multiBackend: false, apiKeyEditable: true, fields: ['base_url', 'provider', 'model'] },
  { key: 'asr', multiBackend: false, apiKeyEditable: true, fields: ['base_url', 'model'] },
  {
    key: 'video',
    multiBackend: false,
    apiKeyEditable: false,
    fields: ['base_url', 'workflow_template', 'width', 'height', 'fps', 'duration', 'steps'],
  },
  { key: 'rerank', multiBackend: false, apiKeyEditable: false, fields: ['endpoint', 'model', 'top_n'] },
  {
    key: 'comfyui',
    multiBackend: false,
    apiKeyEditable: false,
    fields: ['base_url', 'unet_gguf', 'clip_gguf', 'vae', 'workflow_template', 'width', 'height', 'steps', 'cfg', 'seed'],
  },
]

const CATEGORY_ICON_MAP = {
  llm: BrainCircuit,
  embed: Boxes,
  image: Image,
  audio: Volume2,
  asr: Mic,
  video: Video,
  rerank: ListFilter,
  comfyui: Workflow,
} as const

const IGNORED_BACKEND_KEYS = new Set(['meta', 'api_key'])
const MODEL_FIELD_CANDIDATES = ['model', 'primary_model', 'unet_gguf', 'clip_gguf', 'vae', 'workflow_template']
const DEFAULT_TTS_FALLBACK = { configured: false, primary_failures: 0, fallback_served: 0 }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isEditableValue(value: unknown): value is EditableValue {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}

function normalizeEditableValue(value: unknown): EditableValue {
  return isEditableValue(value) ? value : JSON.stringify(value ?? '')
}

function extractConfig(entry: Record<string, unknown>, preferredFields: string[]): Record<string, EditableValue> {
  const fieldNames = new Set<string>()
  preferredFields.forEach((field) => {
    if (field in entry) fieldNames.add(field)
  })
  Object.keys(entry).forEach((field) => {
    if (!IGNORED_BACKEND_KEYS.has(field)) fieldNames.add(field)
  })

  const config: Record<string, EditableValue> = {}
  fieldNames.forEach((field) => {
    config[field] = normalizeEditableValue(entry[field])
  })
  return config
}

function createEditor(
  definition: CategoryDefinition,
  backendName: string,
  rawEntry: Record<string, unknown>,
): BackendEditor {
  const meta = isRecord(rawEntry.meta) ? (rawEntry.meta as unknown as ModelBackendMeta) : undefined
  const originalConfig = extractConfig(rawEntry, definition.fields)
  const maskedApiKey = typeof rawEntry.api_key === 'string' ? rawEntry.api_key : ''

  return {
    category: definition.key,
    backendName,
    meta,
    originalConfig,
    config: { ...originalConfig },
    maskedApiKey,
    apiKeyValue: '',
    apiKeyTouched: false,
    showApiKey: false,
    discoveredModels: [],
    isTesting: false,
    isDiscovering: false,
    isSaving: false,
    testResult: null,
  }
}

function createEditors(models: Record<string, unknown>): BackendEditor[] {
  return CATEGORY_DEFINITIONS.flatMap((definition) => {
    const rawCategory = models[definition.key]
    if (!isRecord(rawCategory)) return []

    if (definition.multiBackend) {
      const providers = rawCategory.providers
      if (!isRecord(providers)) return []
      return Object.entries(providers)
        .filter(([, rawEntry]) => isRecord(rawEntry))
        .map(([backendName, rawEntry]) => createEditor(definition, backendName, rawEntry as Record<string, unknown>))
    }

    if (!definition.multiBackend) {
      return [createEditor(definition, 'default', rawCategory)]
    }

    return []
  })
}

function getChangedConfig(editor: BackendEditor): Record<string, EditableValue> {
  return Object.fromEntries(
    Object.entries(editor.config).filter(([field, value]) => value !== editor.originalConfig[field]),
  )
}

function buildFullConfigPayload(editor: BackendEditor): Record<string, EditableValue> {
  return Object.fromEntries(
    Object.entries(editor.config).filter(([field, value]) => {
      if (IGNORED_BACKEND_KEYS.has(field)) return false
      if (typeof value === 'number') return Number.isFinite(value)
      return true
    }),
  )
}

function getModelField(config: Record<string, EditableValue>): string | null {
  return MODEL_FIELD_CANDIDATES.find((field) => field in config) ?? null
}

function getBaseUrl(config: Record<string, EditableValue>): string | undefined {
  const value = config.base_url
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function getProbeBaseUrl(config: Record<string, EditableValue>): string | undefined {
  const baseUrl = getBaseUrl(config)
  if (baseUrl) return baseUrl
  const endpoint = config.endpoint
  return typeof endpoint === 'string' && endpoint.trim() ? endpoint.trim() : undefined
}

function parseInputValue(rawValue: string, previousValue: EditableValue): EditableValue {
  if (typeof previousValue === 'number') {
    if (rawValue === '') return null
    const nextValue = Number(rawValue)
    return Number.isFinite(nextValue) ? nextValue : previousValue
  }
  return rawValue
}

function isMultiBackendCategory(category: string): boolean {
  return CATEGORY_DEFINITIONS.some((definition) => definition.key === category && definition.multiBackend)
}

function toIdPart(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_')
}

export default function ModelSettings() {
  const { t } = useTranslation()
  const [editors, setEditors] = useState<BackendEditor[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [healthItems, setHealthItems] = useState<ModelBackendHealthItem[]>([])
  const [healthSummary, setHealthSummary] = useState<ModelBackendHealthResponse['summary']>({ ok: 0, failed: 0, unknown: 0, disabled: 0 })
  const [ttsFallback, setTTSFallback] = useState<NonNullable<ModelBackendHealthResponse['tts_fallback']>>(DEFAULT_TTS_FALLBACK)
  const hasAdminToken = Boolean(getAdminToken())

  const applyHealth = (response: ModelBackendHealthResponse) => {
    const healthByKey = new Map(response.items.map((item) => [healthKey(item.category, item.backend_name), item]))
    setHealthItems(response.items)
    setHealthSummary(response.summary)
    setTTSFallback(response.tts_fallback ?? DEFAULT_TTS_FALLBACK)
    setEditors((current) =>
      current.map((editor) => {
        const item = healthByKey.get(healthKey(editor.category, editor.backendName))
        if (!item) return editor
        if (item.disabled) {
          return {
            ...editor,
            testResult: { ok: false, disabled: true },
          }
        }
        if (item.ok === null) return editor
        return {
          ...editor,
          testResult: { ok: item.ok, unsupported: item.unsupported, message: item.error },
        }
      }),
    )
  }

  const refreshHealth = async () => {
    const response = await getModelBackendHealth()
    applyHealth(response)
    return response
  }

  const runProbe = async (editor: BackendEditor, options: { silent: boolean }) => {
    updateEditor(editor.category, editor.backendName, (current) => ({ ...current, isTesting: true, testResult: options.silent ? current.testResult : null }))
    try {
      const apiKey = editor.apiKeyValue.trim()
      const result = await probeModelBackend(editor.category, {
        backend_name: editor.backendName,
        base_url: getProbeBaseUrl(editor.config),
        ...(editor.apiKeyTouched && apiKey ? { api_key: apiKey } : {}),
      })
      updateEditor(editor.category, editor.backendName, (current) => ({
        ...current,
        isTesting: false,
        testResult: { ok: result.ok, unsupported: result.unsupported, message: result.error },
      }))
      if (!options.silent) {
        if (result.ok) toast.success(t('modelSettings.testPassed'))
        else if (result.unsupported)
          toast.info(result.error ? t('modelSettings.probeUnsupported') : t('modelSettings.probeUnsupportedHint'))
        else toast.error(result.error ? friendlyMessage(new Error(result.error), t('modelSettings.testFailed')) : t('modelSettings.testFailed'))
      }
      await refreshHealth()
      return result
    } catch (error) {
      const message = friendlyMessage(error, t('common.serviceError'))
      updateEditor(editor.category, editor.backendName, (current) => ({
        ...current,
        isTesting: false,
        testResult: { ok: false, unsupported: false, message },
      }))
      if (!options.silent) toast.error(message)
      return { ok: false, error: message }
    }
  }

  const editorsByCategory = useMemo(() => {
    const grouped = new Map<string, BackendEditor[]>()
    editors.forEach((editor) => {
      const next = grouped.get(editor.category) ?? []
      next.push(editor)
      grouped.set(editor.category, next)
    })
    return grouped
  }, [editors])

  useEffect(() => {
    if (!hasAdminToken) return
    let cancelled = false
    setIsLoading(true)
    setLoadError(null)
    getModels()
      .then(async (models) => {
        const nextEditors = createEditors(models)
        if (!cancelled) setEditors(nextEditors)
        try {
          const health = await getModelBackendHealth()
          if (!cancelled) applyHealth(health)
        } catch {
          // Settings still render when the optional health aggregate is unavailable.
        }
        await runProbesWithLimit(nextEditors, (editor) => {
          if (!cancelled) return runProbe(editor, { silent: true })
          return Promise.resolve({ ok: false })
        })
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(friendlyMessage(error, t('common.serviceError')))
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [hasAdminToken])

  const updateEditor = (
    category: string,
    backendName: string,
    updater: (editor: BackendEditor) => BackendEditor,
  ) => {
    setEditors((current) =>
      current.map((editor) =>
        editor.category === category && editor.backendName === backendName ? updater(editor) : editor,
      ),
    )
  }

  const onTest = async (editor: BackendEditor) => {
    await runProbe(editor, { silent: false })
  }

  const onDiscover = async (editor: BackendEditor) => {
    updateEditor(editor.category, editor.backendName, (current) => ({ ...current, isDiscovering: true }))
    try {
      const result = await discoverModels(
        editor.category,
        isMultiBackendCategory(editor.category) ? editor.backendName : undefined,
      )
      updateEditor(editor.category, editor.backendName, (current) => ({
        ...current,
        isDiscovering: false,
        discoveredModels: result.models,
      }))
      if (result.error) toast.error(friendlyMessage(new Error(result.error), t('modelSettings.discoverEmpty')))
      else if (result.models.length) toast.success(t('modelSettings.discoverLoaded', { count: result.models.length }))
      else toast.info(result.message || t('modelSettings.discoverEmpty'))
    } catch (error) {
      const message = friendlyMessage(error, t('common.serviceError'))
      updateEditor(editor.category, editor.backendName, (current) => ({ ...current, isDiscovering: false }))
      toast.error(message)
    }
  }

  const onSave = async (editor: BackendEditor) => {
    const changedConfig = getChangedConfig(editor)
    const fullConfig = buildFullConfigPayload(editor)
    const apiKey = editor.apiKeyValue.trim()
    const shouldSendApiKey = editor.apiKeyTouched && apiKey.length > 0
    if (Object.keys(changedConfig).length === 0 && !shouldSendApiKey) {
      toast.info(t('modelSettings.noChanges'))
      return
    }

    updateEditor(editor.category, editor.backendName, (current) => ({ ...current, isSaving: true }))
    try {
      const result = await putCategory(editor.category, {
        backend_name: editor.backendName,
        // The settings page represents the full desired state for this backend;
        // the server stores this snapshot as the DB overlay instead of merging.
        config: fullConfig,
        ...(shouldSendApiKey ? { api_key: apiKey } : {}),
      })
      updateEditor(editor.category, editor.backendName, (current) => ({
        ...current,
        isSaving: false,
        originalConfig: fullConfig,
        apiKeyValue: '',
        apiKeyTouched: false,
      }))
      toast.success(result.message || (result.hot_reloaded ? t('modelSettings.savedHotReloaded') : t('modelSettings.savedRestart')))
      await runProbe({ ...editor, originalConfig: fullConfig, config: fullConfig, apiKeyValue: '', apiKeyTouched: false }, { silent: true })
    } catch (error) {
      const message = friendlyMessage(error, t('common.serviceError'))
      updateEditor(editor.category, editor.backendName, (current) => ({ ...current, isSaving: false }))
      toast.error(message)
    }
  }

  if (!hasAdminToken) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-6">
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="size-4 text-primary" />
              {t('modelSettings.tokenMissingTitle')}
            </CardTitle>
            <CardDescription>{t('modelSettings.tokenMissingDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/admin">{t('modelSettings.goAdmin')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div className="space-y-2">
        <Button asChild className="-ml-2 h-8 px-2 text-muted-foreground" size="sm" variant="ghost">
          <Link to="/admin">
            <ArrowLeft className="mr-1 size-4" />
            {t('modelSettings.backToAdmin')}
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-normal">{t('modelSettings.title')}</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">{t('modelSettings.subtitle')}</p>
      </div>

      <HealthSummaryPanel items={healthItems} summary={healthSummary} ttsFallback={ttsFallback} />

      {isLoading ? (
        <div className="space-y-4">
          {CATEGORY_DEFINITIONS.slice(0, 3).map((definition) => (
            <Card key={definition.key} className="rounded-lg">
              <CardHeader>
                <Skeleton className="h-5 w-36" />
                <Skeleton className="h-4 w-72" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-28 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : loadError ? (
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle>{t('modelSettings.loadFailed')}</CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.reload()}>{t('common.retry')}</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {CATEGORY_DEFINITIONS.map((definition) => (
            <CategoryCard
              key={definition.key}
              definition={definition}
              editors={editorsByCategory.get(definition.key) ?? []}
              updateEditor={updateEditor}
              onTest={onTest}
              onDiscover={onDiscover}
              onSave={onSave}
            />
          ))}
        </div>
      )}
    </div>
  )
}

async function runProbesWithLimit(
  editors: BackendEditor[],
  probe: (editor: BackendEditor) => Promise<unknown>,
  limit = 3,
) {
  const queue = [...editors]
  const workers = Array.from({ length: Math.min(limit, queue.length) }, async () => {
    while (queue.length > 0) {
      const editor = queue.shift()
      if (editor) await probe(editor)
    }
  })
  await Promise.all(workers)
}

export function HealthSummaryPanel({
  items,
  summary,
  ttsFallback,
}: {
  items: ModelBackendHealthItem[]
  summary: ModelBackendHealthResponse['summary']
  ttsFallback?: ModelBackendHealthResponse['tts_fallback']
}) {
  const { t } = useTranslation()
  const resolvedTTSFallback = ttsFallback ?? DEFAULT_TTS_FALLBACK
  const failedItems = items.filter((item) => !item.disabled && item.ok === false)
  const disabledItems = items.filter((item) => item.disabled)

  return (
    <Card className="rounded-lg">
      <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-sm font-medium">
            <CheckCircle2 className="size-4 text-emerald-600" />
            {t('modelSettings.healthOk', { count: summary.ok })}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-medium">
            <XCircle className="size-4 text-destructive" />
            {t('modelSettings.healthFailed', { count: summary.failed })}
          </span>
          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
            <AlertCircle className="size-4" />
            {t('modelSettings.healthUnknown', { count: summary.unknown })}
          </span>
          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
            <ServerCog className="size-4" />
            {t('modelSettings.healthDisabled', { count: summary.disabled })}
          </span>
          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
            <Volume2 className="size-4" />
            {resolvedTTSFallback.configured
              ? t('modelSettings.ttsFallbackStats', {
                  fallbackServed: resolvedTTSFallback.fallback_served,
                  primaryFailures: resolvedTTSFallback.primary_failures,
                })
              : t('modelSettings.ttsFallbackNotConfigured')}
          </span>
        </div>
        {failedItems.length > 0 || disabledItems.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {failedItems.map((item) => (
              <a
                key={`${item.category}:${item.backend_name}`}
                href={`#model-settings-${toIdPart(item.category)}-${toIdPart(item.backend_name)}`}
              >
                <Badge
                  variant="outline"
                  className="gap-1 border-destructive/40 text-destructive transition-colors hover:bg-destructive/10"
                >
                  <XCircle className="size-3" />
                  {t(`modelSettings.categories.${item.category}`, { defaultValue: item.category })}/{item.backend_name}
                </Badge>
              </a>
            ))}
            {disabledItems.map((item) => (
              <a
                key={`${item.category}:${item.backend_name}`}
                href={`#model-settings-${toIdPart(item.category)}-${toIdPart(item.backend_name)}`}
              >
                <Badge variant="outline" className="gap-1 text-muted-foreground">
                  <ServerCog className="size-3" />
                  {t(`modelSettings.categories.${item.category}`, { defaultValue: item.category })}/{item.backend_name}
                  <span>· {t('modelSettings.backendDisabled')}</span>
                </Badge>
              </a>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

interface CategoryCardProps {
  definition: CategoryDefinition
  editors: BackendEditor[]
  updateEditor: (
    category: string,
    backendName: string,
    updater: (editor: BackendEditor) => BackendEditor,
  ) => void
  onTest: (editor: BackendEditor) => void
  onDiscover: (editor: BackendEditor) => void
  onSave: (editor: BackendEditor) => void
}

function CategoryCard({ definition, editors, updateEditor, onTest, onDiscover, onSave }: CategoryCardProps) {
  const { t } = useTranslation()
  const CategoryIcon = CATEGORY_ICON_MAP[definition.key as keyof typeof CATEGORY_ICON_MAP] || ServerCog

  return (
    <Card className="overflow-hidden rounded-lg border border-border/70 bg-card shadow-sm">
      <CardHeader className="border-b border-border/60 bg-muted/50">
        <CardTitle className="flex items-center gap-2 text-base">
          <CategoryIcon className="size-4 text-primary" />
          {t(`modelSettings.categories.${definition.key}`)}
        </CardTitle>
        <CardDescription className="leading-6 text-muted-foreground/90">
          {t(`modelSettings.categoryDescriptions.${definition.key}`)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {editors.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('modelSettings.emptyCategory')}</p>
        ) : (
          <div className="space-y-4">
            {editors.map((editor, index) => (
              <div key={`${editor.category}:${editor.backendName}`} className="space-y-4">
                {index > 0 ? <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" /> : null}
                <div className="rounded-lg border border-border/50 bg-background/70 p-4">
                  <BackendSection
                    definition={definition}
                    editor={editor}
                    updateEditor={updateEditor}
                    onTest={onTest}
                    onDiscover={onDiscover}
                    onSave={onSave}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface BackendSectionProps {
  definition: CategoryDefinition
  editor: BackendEditor
  updateEditor: CategoryCardProps['updateEditor']
  onTest: (editor: BackendEditor) => void
  onDiscover: (editor: BackendEditor) => void
  onSave: (editor: BackendEditor) => void
}

function BackendSection({ definition, editor, updateEditor, onTest, onDiscover, onSave }: BackendSectionProps) {
  const { t } = useTranslation()
  const changedConfig = getChangedConfig(editor)
  const isDirty = Object.keys(changedConfig).length > 0 || (editor.apiKeyTouched && editor.apiKeyValue.trim().length > 0)
  const modelField = getModelField(editor.config)
  const canEditApiKey = definition.apiKeyEditable && (editor.meta?.has_api_key ?? true)
  const isDisabled = editor.testResult?.disabled === true || editor.config.enabled === false

  const setConfigField = (field: string, value: EditableValue) => {
    updateEditor(editor.category, editor.backendName, (current) => ({
      ...current,
      config: { ...current.config, [field]: value },
    }))
  }

  return (
    <section id={`model-settings-${toIdPart(editor.category)}-${toIdPart(editor.backendName)}`} className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold">{definition.multiBackend ? editor.backendName : t('modelSettings.defaultBackend')}</h2>
        {editor.meta?.db_overridden ? <Badge variant="secondary">{t('modelSettings.dbOverridden')}</Badge> : null}
        {canEditApiKey && editor.meta?.has_api_key ? <Badge variant="outline">{t('modelSettings.apiKeyStored')}</Badge> : null}
        {isDirty ? <Badge>{t('modelSettings.dirty')}</Badge> : null}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {Object.entries(editor.config).map(([field, value]) => (
          <ConfigField
            key={field}
            idPrefix={`model-settings-${toIdPart(editor.category)}-${toIdPart(editor.backendName)}`}
            field={field}
            value={value}
            discoveredModels={modelField === field ? editor.discoveredModels : []}
            onChange={(nextValue) => setConfigField(field, nextValue)}
          />
        ))}
        {canEditApiKey ? (
          <ApiKeyField
            id={`model-settings-${toIdPart(editor.category)}-${toIdPart(editor.backendName)}-api-key`}
            value={editor.apiKeyValue}
            maskedValue={editor.maskedApiKey}
            show={editor.showApiKey}
            onChange={(value) =>
              updateEditor(editor.category, editor.backendName, (current) => ({
                ...current,
                apiKeyValue: value,
                apiKeyTouched: true,
              }))
            }
            onToggle={() =>
              updateEditor(editor.category, editor.backendName, (current) => ({
                ...current,
                showApiKey: !current.showApiKey,
              }))
            }
          />
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => onTest(editor)} disabled={editor.isTesting}>
          {editor.isTesting ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          {t('modelSettings.test')}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => onDiscover(editor)} disabled={editor.isDiscovering}>
          {editor.isDiscovering ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          {t('modelSettings.discover')}
        </Button>
        <Button type="button" size="sm" onClick={() => onSave(editor)} disabled={editor.isSaving || !isDirty}>
          {editor.isSaving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {t('common.save')}
        </Button>
        <BackendHealthBadge disabled={isDisabled} result={editor.testResult} />
      </div>
    </section>
  )
}

export function BackendHealthBadge({
  disabled,
  result,
}: {
  disabled: boolean
  result: BackendEditor['testResult']
}) {
  const { t } = useTranslation()

  if (disabled) {
    return (
      <Badge variant="outline" className="gap-1.5 text-muted-foreground">
        <ServerCog className="size-4" />
        {t('modelSettings.backendDisabled')}
      </Badge>
    )
  }
  if (!result) return null

  return (
    <span className="inline-flex items-center gap-2">
      {result.ok ? (
        <Badge variant="secondary" className="gap-1.5">
          <CheckCircle2 className="size-4 text-emerald-600" />
          {t('modelSettings.testPassed')}
        </Badge>
      ) : result.unsupported ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="gap-1.5">
              {t('modelSettings.probeUnsupported')}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{t('modelSettings.probeUnsupportedHint')}</TooltipContent>
        </Tooltip>
      ) : (
        <Badge variant="destructive" className="gap-1.5">
          <XCircle className="size-4" />
          {result.message || t('modelSettings.testFailed')}
        </Badge>
      )}
    </span>
  )
}

interface ConfigFieldProps {
  idPrefix: string
  field: string
  value: EditableValue
  discoveredModels: string[]
  onChange: (value: EditableValue) => void
}

function ConfigField({ idPrefix, field, value, discoveredModels, onChange }: ConfigFieldProps) {
  const { t } = useTranslation()
  const id = `${idPrefix}-${toIdPart(field)}`
  const isLongText = field === 'workflow_template' && typeof value === 'string' && value.length > 80
  const hint = t(`modelSettings.fieldHints.${field}`, { defaultValue: '' })

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{t(`modelSettings.fields.${field}`, { defaultValue: field })}</Label>
      {discoveredModels.length > 0 ? (
        <Select value={typeof value === 'string' ? value : undefined} onValueChange={onChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('modelSettings.selectDiscoveredModel')} />
          </SelectTrigger>
          <SelectContent>
            {discoveredModels.map((model) => (
              <SelectItem key={model} value={model}>
                {model}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      {isLongText ? (
        <textarea
          id={id}
          className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          value={String(value ?? '')}
          placeholder={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : typeof value === 'boolean' ? (
        <Select value={value ? 'true' : 'false'} onValueChange={(nextValue) => onChange(nextValue === 'true')}>
          <SelectTrigger id={id} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">{t('modelSettings.booleanTrue')}</SelectItem>
            <SelectItem value="false">{t('modelSettings.booleanFalse')}</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={id}
          type={typeof value === 'number' ? 'number' : 'text'}
          value={String(value ?? '')}
          placeholder={String(value ?? '')}
          onChange={(event) => onChange(parseInputValue(event.target.value, value))}
        />
      )}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

interface ApiKeyFieldProps {
  id: string
  value: string
  maskedValue: string
  show: boolean
  onChange: (value: string) => void
  onToggle: () => void
}

function ApiKeyField({ id, value, maskedValue, show, onChange, onToggle }: ApiKeyFieldProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{t('modelSettings.fields.api_key')}</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          autoComplete="off"
          placeholder={maskedValue || t('modelSettings.apiKeyPlaceholder')}
          onChange={(event) => onChange(event.target.value)}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onToggle}
          aria-label={show ? t('admin.hideToken') : t('admin.showToken')}
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t('modelSettings.apiKeyHint')}</p>
    </div>
  )
}
